import { serverPdfError, bulkPdfError } from './bulkPdfErrors.js'
import { assertResearchPermission, researchIdentity, richReportRequest } from './researchPermissions.js'
import { createAxiosInstance } from '@/plugins/axios.js'
import { selectedCohort, selectedCohortState, saveSelectedCohort, reconcileSelectedCohort } from './cohortSelection.js'
import { L } from '@/plugins/utils/i18n';

const client = () => {
  const instance = createAxiosInstance(L.cohort_data_server)
  if (typeof window !== 'undefined' && window.location.port === '8784') instance.defaults.baseURL = '/a/'
  return instance
}
const unwrap = response => response?.data?.data ?? response?.data ?? null

function _siteParam(){ return selectedCohort()?.site || undefined }
function _queryParams(org,params={}){
  const site = params.site ?? _siteParam()
  const { site: _ignoredSite, ...rest } = params
  return site ? { org, site, ...rest } : { org, ...rest }
}

export async function listCohortOrgs(){ return unwrap(await client().get('/cohort/orgs'))?.nodes || [] }
export async function listCohortSites(org){ return unwrap(await client().get('/cohort/sites',{params:{org}})) || {nodes:[]} }
export async function assignSite(org,site,devices){assertResearchPermission('admin'); return unwrap(await client().post('/cohort/sites/assign',{site,devices},{params:{org}})) }
export async function unassignSite(org,devices){assertResearchPermission('admin'); return unwrap(await client().post('/cohort/sites/unassign',{devices},{params:{org}})) }
export async function getCohortSnapshot(org,params={}){ return unwrap(await client().get('/cohort/snapshot',{params:_queryParams(org,params)})) }
export async function getCohortRealtime(org,params={}){ return unwrap(await client().get('/cohort/realtime',{params:_queryParams(org,params),timeout:15000})) }
export async function getCohortSubjects(org,params={}){ return unwrap(await client().get('/cohort/subjects',{params:_queryParams(org,params)})) }
export async function getCohortSubjectDeviceHistory(org,subjectId,params={}){ return unwrap(await client().get(`/cohort/subjects/${encodeURIComponent(subjectId)}/device-history`,{params:_queryParams(org,params)})) }
export async function getCohortDevices(org,params={},signal){ return unwrap(await client().get('/cohort/devices',{params:_queryParams(org,params),signal})) }
export async function getCohortState(org){ return unwrap(await client().get('/cohort/state',{params:{org}})) }
// Re-export through bindings so legacy consumers share the canonical selection source.
export { selectedCohort, selectedCohortState, saveSelectedCohort, reconcileSelectedCohort }
export async function getCohortReportSubjects(org,params={}){ return unwrap(await client().get('/cohort/report-subjects',{params:_queryParams(org,params)})) }
// Always bounded, including export callers. Explicit site freezes the request scope.
export async function getDeviceReports(org,params={},signal){
  assertResearchPermission('edit')
  const { device, date_from, date_to, ...rest } = params
  const search = String(device ?? params.search ?? '').trim()
  if (!search) throw new Error('Exact device is required')
  const bounded = { ...rest,
    search,
    ...(date_from ? { date_from } : {}),
    ...(date_to ? { date_to } : {}),
    page: Math.max(1, Math.floor(Number(params.page) || 1)),
    page_size: Math.max(1, Math.min(100, Math.floor(Number(params.page_size) || 20))),
  }
  return unwrap(await client().get('/cohort/device-reports',{params:_queryParams(org,bounded),signal}))
}
export async function getCohortSubjectNights(org,subjectId,params={}){ return unwrap(await client().get(`/cohort/report-subjects/${encodeURIComponent(subjectId)}/nights`,{params:{org,...params}})) }
export async function getRichReports(org,params={}){
  assertResearchPermission('edit')
  const request = richReportRequest(researchIdentity.value.user, params)
  const data = unwrap(await client().get(request.path,{params:_queryParams(org,request.params)}))
  const partial = request.path.endsWith('/view')
  return { ...data, partial, scope: partial ? 'page-only' : 'legacy', pageNotice: partial ? '当前页' : '' }
}
export async function getRotations(org,params={}){ return unwrap(await client().get('/cohort/rotations',{params:_queryParams(org,params)})) }
export async function getNightReportEntry(org,device,date){return unwrap(await client().get(`/cohort/reports/${encodeURIComponent(device)}/${date}/entry`,{params:{org}}))}

// Reuse the normal authenticated Axios client. No new token, URL credential,
// browser renderer, or alternate PDF download endpoint.
export async function downloadNightPdf(org, device, date, subjectId, locale, signal) {
  assertResearchPermission('export')
  try {
    const response = await client().post('/api/v1/orgs/reports/pdf',
      { org, device, date, subjectId, locale: locale === 'en' ? 'en' : 'zh' },
      { responseType: 'blob', timeout: 90000, signal })
    const blob = response.data
    const type = String(response.headers?.['content-type'] || blob?.type || '').split(';')[0].trim().toLowerCase()
    if (!(blob instanceof Blob) || type !== 'application/pdf' || await blob.slice(0, 5).text() !== '%PDF-') throw bulkPdfError('render')
    if (signal?.aborted) throw bulkPdfError('cancelled')
    assertResearchPermission('export')
    return blob
  } catch (error) { throw await serverPdfError(error) }
}

export async function updateCohortConfig(org,config){assertResearchPermission('admin');return unwrap(await client().put('/cohort/config',config,{params:{org}}))}
const PDF_BATCH_CODE = {
  PDF_BUSY: 'busy', PDF_TIMEOUT: 'gateway-timeout', PDF_CLIENT_DISCONNECTED: 'cancelled',
  PDF_REPORT_NOT_READY: 'render', PDF_RENDER_FAILED: 'render', PDF_REPORT_DENIED: 'forbidden',
  PDF_EXPORT_DENIED: 'forbidden', PDF_ENTRY_UNAVAILABLE: 'unavailable', PDF_UNAVAILABLE: 'unavailable',
  PDF_PERMISSION_UNAVAILABLE: 'unavailable', PDF_AUTH_REQUIRED: 'unauthorized', PDF_INVALID_REQUEST: 'render',
}
export function pdfBatchFailureCode(code) { return PDF_BATCH_CODE[code] || 'render' }

function waitForBatch(ms, signal) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => { signal?.removeEventListener('abort', abort); resolve() }, ms)
    const abort = () => { clearTimeout(timer); reject(bulkPdfError('cancelled')) }
    if (signal?.aborted) { abort(); return }
    signal?.addEventListener('abort', abort, { once: true })
  })
}

// The server renders every night and packs the ZIP. This call only submits the
// list, reports progress, and accepts the finished archive.
export async function downloadNightPdfBatch(nights, locale, signal, onProgress) {
  assertResearchPermission('export')
  const payload = {
    nights: nights.map(night => ({
      org: night.org, device: night.device, date: night.date, subjectId: night.subjectId,
      locale: locale === 'en' ? 'en' : 'zh', entryName: night.entryName,
    })),
  }
  let jobId = ''
  try {
    const created = await client().post('/api/v1/orgs/reports/pdf-batch', payload, { signal, timeout: 30000 })
    jobId = created.data?.jobId
    if (typeof jobId !== 'string' || !/^[\w-]{20,80}$/.test(jobId)) throw bulkPdfError('unavailable')
    const started = Date.now()
    let status
    while (Date.now() - started < 45 * 60 * 1000) {
      status = (await client().get(`/api/v1/orgs/reports/pdf-batch/${jobId}`, { signal, timeout: 15000 })).data
      if (onProgress) onProgress(status)
      if (status?.state === 'done' || status?.state === 'failed' || status?.state === 'cancelled') break
      await waitForBatch(1000, signal)
    }
    if (status?.state === 'cancelled') throw bulkPdfError('cancelled')
    if (status?.state !== 'done' || status.ready !== true) {
      const error = bulkPdfError(pdfBatchFailureCode(status?.failures?.[0]?.code))
      error.failures = status?.failures || []
      throw error
    }
    const file = await client().get(`/api/v1/orgs/reports/pdf-batch/${jobId}/file`, { responseType: 'blob', timeout: 120000, signal })
    const blob = file.data
    const type = String(file.headers?.['content-type'] || blob?.type || '').split(';')[0].trim().toLowerCase()
    if (!(blob instanceof Blob) || type !== 'application/zip' || await blob.slice(0, 2).text() !== 'PK') throw bulkPdfError('render')
    if (signal?.aborted) throw bulkPdfError('cancelled')
    assertResearchPermission('export')
    return { blob, failures: status.failures || [] }
  } catch (error) {
    if (jobId && (signal?.aborted || error?.code === 'cancelled' || error?.code === 'ERR_CANCELED')) {
      client().post(`/api/v1/orgs/reports/pdf-batch/${jobId}/cancel`, {}, { timeout: 10000 }).catch(() => {})
    }
    if (error?.failures || error?.code === 'cancelled') throw error
    throw await serverPdfError(error)
  }
}

export async function updateCohortSubject(org,subjectId,patch){assertResearchPermission('edit');if (!Object.keys(patch).length || Object.keys(patch).some(key => !['note','status'].includes(key))) assertResearchPermission('admin');return unwrap(await client().patch(`/cohort/subjects/${encodeURIComponent(subjectId)}`,patch,{params:{org}}))}
export async function deleteCohortSubject(org,subjectId,params={}){assertResearchPermission('admin');return unwrap(await client().delete(`/cohort/subjects/${encodeURIComponent(subjectId)}`,{params:_queryParams(org,params)}))}
export async function addManualCohortSubject(org,payload){assertResearchPermission('edit');return unwrap(await client().post('/cohort/subjects/manual',payload,{params:{org}}))}
export async function rebindCohortSubject(org,subjectId,payload){assertResearchPermission('edit');return unwrap(await client().post(`/cohort/subjects/${encodeURIComponent(subjectId)}/rebind`,payload,{params:{org}}))}
export async function unbindCohortSubject(org,subjectId,payload={}){assertResearchPermission('edit');return unwrap(await client().post(`/cohort/subjects/${encodeURIComponent(subjectId)}/unbind`,payload,{params:{org}}))}
export async function previewDeviceBeds(org,rows){assertResearchPermission('edit');return unwrap(await client().post('/cohort/device-beds/preview',{rows},{params:{org}}))}
export async function importDeviceBeds(org,rows){assertResearchPermission('edit');return unwrap(await client().post('/cohort/device-beds/import',{rows},{params:{org}}))}
export async function getDeviceBedHistory(org,device,limit=100){return unwrap(await client().get('/cohort/device-beds/history',{params:{org,device,limit}}))}

export function getChannelDataUrl(org,device,date,format='csv'){assertResearchPermission('export');
  const base=client().defaults.baseURL||''
  return `${base}/cohort/channel-data/${encodeURIComponent(device)}/${date}?org=${encodeURIComponent(org)}&format=${format}`
}
export async function downloadChannelData(org,device,date){assertResearchPermission('export');
  const resp=await client().get(`/cohort/channel-data/${encodeURIComponent(device)}/${date}`,{params:{org,format:'csv'},responseType:'blob'})
  return resp.data
}

/**
 * AI Chat — 流式对话
 * @param {Array} messages - [{role:'user'|'assistant', content:'...'}]
 * @param {Function} onDelta - (text) => void  逐字回调
 * @param {AbortSignal} [signal] - 可选取消信号
 * @returns {Promise<void>} resolves when stream ends
 */
export async function streamAiChat(messages, onDelta, signal) {assertResearchPermission('admin');
  const baseURL = client().defaults.baseURL || ''
  const token = localStorage.getItem('authToken_org') || ''
  const authHeader = token.startsWith('Bearer') ? token : `Bearer ${token}`

  const response = await fetch(`${baseURL}/ai/chat`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': authHeader,
    },
    body: JSON.stringify({ messages }),
    signal,
  })

  if (!response.ok) {
    const err = await response.json().catch(() => ({}))
    throw new Error(err.detail || `请求失败 (${response.status})`)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  function processLine(line) {
    if (!line.startsWith('data: ')) return false
    const dataStr = line.slice(6).trim()
    if (!dataStr) return false
    try {
      const event = JSON.parse(dataStr)
      if (event.type === 'delta' && event.text) {
        onDelta(event.text)
      } else if (event.type === 'error') {
        throw new Error(event.error || 'AI 服务异常')
      } else if (event.type === 'done') {
        return true // signal stream end
      }
    } catch (e) {
      if (e.message && !e.message.includes('JSON')) throw e
    }
    return false
  }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() || ''

    for (const line of lines) {
      if (processLine(line)) return
    }
  }

  // Flush remaining buffer (handles case where stream ends without trailing newline)
  if (buffer.trim()) {
    processLine(buffer)
  }
}

export async function getSubjectBasicInfo(org, subjectId, asOf) {
  return unwrap(await client().get(`/cohort/subjects/${encodeURIComponent(subjectId)}/basic-info`, { params: { org, ...(asOf ? { asOf } : {}) } }))
}
export async function patchSubjectBasicInfo(org, subjectId, patch) {assertResearchPermission('edit');
  return unwrap(await client().patch(`/cohort/subjects/${encodeURIComponent(subjectId)}/basic-info`, patch, { params: { org } }))
}

// Clinical documents must use the frozen panel site, not the current localStorage selection.
const clinicalPath = subjectId => `/cohort/subjects/${encodeURIComponent(subjectId)}/clinical-documents`
const clinicalParams = (org, site, extra = {}) => ({ org, site: site ?? '', ...extra })
export async function listSubjectClinicalDocuments(org, subjectId, site, page = 1, signal) {
  return unwrap(await client().get(clinicalPath(subjectId), { params: clinicalParams(org, site, { page, page_size: 20 }), signal }))
}
export async function uploadSubjectClinicalDocument(org, subjectId, site, file, signal) {assertResearchPermission('edit');
  const form = new FormData()
  form.append('file', file)
  return unwrap(await client().post(clinicalPath(subjectId), form, {
    params: clinicalParams(org, site), signal, timeout: 90000,
    headers: { 'Content-Type': undefined }, // Let the browser add the multipart boundary, retaining existing auth.
  }))
}
export async function confirmSubjectClinicalDocument(org, subjectId, site, documentId, payload, signal) {assertResearchPermission('edit');
  return unwrap(await client().patch(`${clinicalPath(subjectId)}/${encodeURIComponent(documentId)}`, payload, { params: clinicalParams(org, site), signal }))
}
export async function downloadSubjectClinicalSource(org, subjectId, site, documentId, signal) {assertResearchPermission('export');
  const response = await client().get(`${clinicalPath(subjectId)}/${encodeURIComponent(documentId)}/source`, { params: clinicalParams(org, site), responseType: 'blob', signal })
  return response.data
}

// Use the same final report payload as the single-night chart; failures stay visible.
export async function getStageExportReport(device,date){assertResearchPermission('export');
  const result=unwrap(await client().get(`/devicereport/${encodeURIComponent(device)}/${encodeURIComponent(date)}/`))
  if(!result?.final)throw new Error(L.cohort_stage_export_missing)
  return result.final
}

// Medication scope is explicit and frozen; never use _queryParams/localStorage site fallback.
const medicationPath = subjectId => `/cohort/subjects/${encodeURIComponent(subjectId)}/medications`
export async function getSubjectMedications(org, subjectId, site, dateFrom, dateTo, signal) {
  return unwrap(await client().get(medicationPath(subjectId), { params: { org, site: site ?? '', date_from: dateFrom, date_to: dateTo }, signal }))
}
export async function createSubjectMedication(org, subjectId, site, payload, signal) {assertResearchPermission('edit');
  return unwrap(await client().post(medicationPath(subjectId), payload, { params: { org, site: site ?? '' }, signal }))
}
export async function patchSubjectMedication(org, subjectId, site, recordId, payload, signal) {assertResearchPermission('edit');
  return unwrap(await client().patch(`${medicationPath(subjectId)}/${encodeURIComponent(recordId)}`, payload, { params: { org, site: site ?? '' }, signal }))
}

// Frozen report ownership is authoritative even after device transfer/deletion.
// One exact, bounded history probe without dates avoids subject-derived windows
// hiding older reports. A nondevice/name query has no exact reports and falls
// back to the original subject search; no current-inventory scans are needed.
export async function resolveExactCohortDevice(org, query, site, signal) {
  const keyword = String(query || '').trim()
  if (!keyword) return null
  if (signal?.aborted) throw new Error('Device resolution cancelled')
  const data = await getDeviceReports(org, { device: keyword, site, page: 1, page_size: 1 }, signal)
  if (signal?.aborted) throw new Error('Device resolution cancelled')
  if (!Array.isArray(data?.nodes) || !Number.isInteger(data.total) || data.total < 0 ||
      data.page !== 1 || data.pageSize !== 1 || data.totalPages !== Math.max(1,data.total) ||
      data.nodes.length !== Math.min(1,data.total) || data.nodes.some(node => node?.device !== keyword)) {
    throw new Error('Incomplete scoped device history lookup')
  }
  return data.total > 0 ? keyword : null
}

export async function getCohortDeviceMovement({ device, site, ...params }, signal) {
  // Empty means all sites, not exact site=''. Never consult mutable selection.
  const frozenParams = site ? { ...params, site } : params
  return unwrap(await client().get(`/cohort/device-movement/${encodeURIComponent(device)}`, { params: frozenParams, signal, timeout: 2500 }))
}
